import {
  DataViewQuickFilter,
  FilterExpression,
  ReferenceProperty,
} from '@osos/shared/api';
import { ALLOWED_MIME_TYPES, MAX_FILE_SIZE_IN_BYTES } from '../../consts';
import { DataEntrySourceModel } from '../data-entry-source';
import { EntityViewTypeEnum } from '../entity-view-type';
import {
  AfterSaveActionEnum,
  DataRelationTypeEnum,
  DataViewScreenTypeEnum,
  EntryActionOperationEnum,
  TableRowClickModeEnum,
} from '../enums';
import { ShowStepOwnerModeEnum } from '../enums/data-view-show-step-owner-mode-enum.enum';
import { MentionsListModeEnum } from '../enums/mentions-list-mode.enum';
import { ShowEmailConfigTypeEnum } from '../enums/show-email-config-type-enum';
import { ValueSourceEnum } from '../enums/value-source-type.enum';
import { IconModel } from '../icon.model';
import { LocalizedValueModel } from '../localized-value.model';
import {
  AnonymousFormModel,
  AuthenticatedFormModel,
  PermissionModel,
} from '../permissions';
import { RefProperty } from '../ref-property.model';
import { SideBarConfigModel } from '../sidebar-config.model';
import { Sort } from '../sort.model';
import { ApplicationSharingPresenter } from './application-sharing-presnter-model';
import { TemplateParameterTypesModel } from './entity-presenter.model';
import { FieldPresenterModel } from './field-presenter.model';

export interface DataViewScreenOrderItem {
  screenType: DataViewScreenTypeEnum;
  order: number;
}

export enum ViewRedirectionScreenTypeEnum {
  EntriesList = 0,
  CreateEntry = 1,
  EntryDetails = 2,
  Dashboard = 3,
}

export class DataViewsPresenterModel {
  dataEntity: string;
  module?: string;
  dataSource: DataEntrySourceModel;
  sourceOptions?: ViewSourceOptions;
  displayConfig?: ViewDisplayOptions;
  description?: LocalizedValueModel;
  editConfiguration?: DataViewEditConfiguration;
  fields: Array<string>;
  icon?: IconModel;
  id?: string;
  isPinned?: boolean;
  isPublished?: boolean;
  isImplicit?: boolean;
  name: LocalizedValueModel;
  domain?: RefProperty;
  filterFirstConfigDto?: null | DataViewFilterFirstConfigurationDto;

  sharing: ApplicationSharingPresenter;
  updateIdentifier?: string;
  specificationVersion: number;
  viewConfiguration: DataViewDisplayConfiguration;
  entriesConfiguration: DataViewEntriesConfiguration;
  permissions?: Array<PermissionModel>;
  anonymous?: AnonymousFormModel;
  authenticated?: AuthenticatedFormModel;
  dashboardConfiguration?: DataViewDashboardConfiguration;
  sideBarConfig?: SideBarConfigModel;
  displayOrder?: number;
  relations?: Array<DataViewRelationModel>;
  systemButtons?: Array<DataViewSystemButton>;
  systemButtonsOrder?: Array<string>;
  screenOrder?: Array<DataViewScreenOrderItem>;
  uiAttributes?: DataViewStyle;
  implicitType?: ViewImplicitType;
  redirectionRules: string;
  qrCode?: DataViewQrCodeConfigurationModel;
  help: ContextualHelpModel;
  screen?: ViewRedirectionScreenTypeEnum;
  defaultViewTemplate?: ViewDisplayTemplate;
}
export class ViewDisplayTemplate {
  parameters?: null | TemplateParameterTypesModel;
  templateId: string;
}

export interface DataViewShowInformationTab {
  /**
   * Whether the information tab will be displayed in the view or not. By default, false. Fallback is false.
   */
  enabled: boolean | null;
  metadata?: {
    /**
     * Whether the metadata will be displayed in the information tab or not.
     */
    enabled: boolean;
    /**
     * Whether the metadata createdAt will be displayed in the information tab or not.
     */
    createdAt: boolean;
    /**
     * Whether the metadata updatedAt will be displayed in the information tab or not.
     */
    updatedAt: boolean;
    /**
     * Whether the metadata createdBy will be displayed in the information tab or not.
     */
    createdBy: boolean;
    /**
     * Whether the metadata updatedBy will be displayed in the information tab or not.
     */
    updatedBy: boolean;
  } | null;
  attachmentsEnabled?: {
    enabled: boolean;
    /**
     * Whether the field attachments will be displayed in the information tab or not.
     */
    fieldAttachments: boolean;
    /**
     * Whether the discussion attachments tab will be displayed in the information tab or not.
     */
    discussionAttachments: boolean;
  };
}
export class ViewDataEntityForAdminPresenterModel {
  description?: LocalizedValueModel;
  id: string;
  name: LocalizedValueModel;
}
export class DataViewsForAdminPresenterModel {
  anonymous: boolean;
  archive: boolean;
  askUser: boolean;
  authenticated: boolean;
  create: boolean;
  dashboard: boolean;
  dataEntity: ViewDataEntityForAdminPresenterModel;
  dataModule: ViewDataEntityForAdminPresenterModel;
  description?: LocalizedValueModel;
  edit: boolean;
  entriesList: boolean;
  entryDetails: boolean;
  filterFirst: boolean;
  groups?: ReferenceProperty[];
  id: string;
  isPublished: boolean;
  name: LocalizedValueModel;
  restricted: boolean;
  users?: ReferenceProperty[];
}

export interface DataViewStyle {
  cssStyle: ViewStyleConfig;
  fieldClasses: Record<string, ViewStyleConfig>;
}
export interface ViewStyleConfig {
  general?: string;
  entryDetails?: string;
  editEntry?: string;
  createEntry?: string;
  askForUser?: string;
  dashboard?: string;
  /*Faces*/
  table?: string;
  calender?: string;
  badge?: string;
  kanban?: string;
  tree?: string;
  miniCard?: string;
}

export type EnabledViewStyleConfig = {
  [Property in keyof ViewStyleConfig]: boolean;
};
export type DataViewFieldsHelpModel = {
  fieldName: string;
  contextualHelp?: LocalizedValueModel;
};

export interface ContextualHelpModel {
  contextualHelp: LocalizedValueModel;
  fieldsContextualHelp?: DataViewFieldsHelpModel[];
  contextualHelpDetails: {
    entriesListHelp?: LocalizedValueModel;
    entryDetailsHelp?: LocalizedValueModel;
    createEntryHelp?: LocalizedValueModel;
    editEntryHelp?: LocalizedValueModel;
    dashboardHelp?: LocalizedValueModel;
    askUserHelp?: LocalizedValueModel;
    entriesListFieldsHelp?: DataViewFieldsHelpModel[];
    entryDetailsFieldsHelp?: DataViewFieldsHelpModel[];
    createEntryFieldsHelp?: DataViewFieldsHelpModel[];
    editEntryFieldsHelp?: DataViewFieldsHelpModel[];
    askUserFieldsHelp?: DataViewFieldsHelpModel[];
  };
}
export interface DataViewSystemButton {
  systemButton: RefProperty;
  redirectionView?: string;
}

export interface DataViewFilterFirstFieldModel {
  fieldDefinition?: FieldPresenterModel;

  /**
   * The original entity id of the field
   */
  fieldEntityId?: string;
  fieldName: string;
}

export interface DataViewFilters {
  viewConfigurationEnabled?: boolean;
  filterFirstDisabled?: boolean;
  filterViewType?: EntityViewTypeEnum;
  published?: boolean;
  pinnedToSideBar?: boolean;
  dataSourceType?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  dataSourceQuery?: string;
  withAllEntriesSourceType?: boolean;
}

export class DataViewEntriesConfiguration {
  customTemplate?: DataViewCustomTemplateConfiguration;
  discussions?: DataViewEntriesDiscussionConfiguration;
  enabled: boolean;
  fields?: Array<string>;
  history?: DataViewEntryHistoryConfiguration;
  supportPaginationBetweenEntries?: boolean;
  showWorkflowHistory?: ShowWorkflowConfig;
  allowedTemplates?: Array<string>;
  showEmailConfig?: Array<ShowEmailConfig>;
  defaultSidebar?: DefaultSidebarEnum;
  showInformation?: DataViewShowInformationTab;
}

export interface ShowEmailConfig {
  type: ShowEmailConfigTypeEnum;
  allEmails: boolean;
  onlySentToMe: boolean;
  onlySentByMe: boolean;
}
export interface ShowWorkflowConfig {
  enabled: boolean;
  showStepOwners?: ShowWorkflowStepOwnerConfig;
  enableHistoryPath?: boolean;
}
export interface ShowWorkflowStepOwnerConfig {
  enabled: boolean;
  showStepOwner?: ShowStepOwnerModeEnum;
}
export type DataViewEntryHistoryConfiguration = {
  enabled: boolean;
  showChanges: boolean;
  showChanger: boolean;
  showTriggerHistory?: {
    enabled: boolean;
    showTriggerDetails: boolean;
  };
};

export type DataViewCustomTemplateConfiguration = {
  enabled: boolean;
  componentName: string;
};

export type DataViewEntriesDiscussionConfiguration = {
  enabled: boolean;
  allowAddComments?: boolean;
  showWorkflowComments: boolean;
  allowMentions: boolean;
  mentionsList?: {
    /**
     * the mentions list mode to be used when mentions is called from view
     */
    mode: MentionsListModeEnum;
    /**
     * The custom mentions in case mode is Custom
     */
    customMentions?: {
      /**
       * Fixed or Dynamic
       */
      sourceType: ValueSourceEnum;
      sourceFields: string[];
      users: ReferenceProperty[];
      groups: ReferenceProperty[];
    };
  };
  allowAudience?: boolean;
  allowAddAttachments?: DataViewEntriesDiscussionAttachmentConfiguration;
  limitCommentsToCurrentView?: boolean;
};

export class DataViewEntriesDiscussionAttachmentConfiguration {
  allowedExtensions?: Array<string>;
  enabled: boolean;
  maxSize?: number;
}

export interface ViewOverrideOptions {
  expression?: string;
  partialView?: any;
}

export interface DataViewRelationModel {
  relationId: string;
  relationType: DataRelationTypeEnum;
  viewId?: string;
  showAsBlock: boolean;
  displayAs?: DisplayRelationAsEnum;
  subViewMode?: RelationSubViewModeEnum;
  openEntryAs?: RelationOpenEntryAs;
  viewOverrideOptions?: ViewOverrideOptions;
}

export type DataViewDashboardConfiguration = {
  enabled: boolean;
  clickToBrowse?: boolean;
  dashboards?: Array<DataViewDashboardDefinition>;
  statisticQueriesMatchingSource: boolean;
  overrideSourceForStatisticQuery?: boolean;
  showEntityOverview: boolean;
  isDefaultLandingPage: boolean;
};

export type DataViewDashboardDefinition = {
  dashboard: RefProperty;
  elements?: Array<string>;
};

export class DataViewEditConfiguration {
  enabled: boolean;
  allowCreateEntryAsDraft?: boolean;
  allowQuickCreate?: boolean;
  allowDuplicateEntry?: boolean;
  showWorkflowAction?: boolean;
  showSidebar?: boolean;
  supportedOperations?: DataViewEditSupportedOperationModel[];
  adminActions?: ViewEditConfigurationAdminActionsPresenterModel;
}

export class ViewEditConfigurationAdminActionsPresenterModel {
  editAsAdmin?: boolean;

  viewAsAdmin?: boolean;

  executeAsAdmin?: boolean;

  closeWorkflow?: boolean;

  moveToStep?: boolean;

  returnToStep?: boolean;

  resetWorkflow?: boolean;

  changeStepOwnerAsAdmin?: boolean;
  changeStepOwnerAsAdminPermanently?: boolean;
}
export class DataViewEditSupportedOperationModel {
  /**
   * The supported operation on the entry
   */
  operationType: EntryActionOperationEnum;
  fields?: Array<string>;
  requiredFields: Array<string>;
  /**
   * The action to execute after save operation on the entry
   */
  afterSave?: ViewAfterSaveAction;
  allowInlineEdit?: boolean;
  allowCreateEntryAsDraft?: boolean;
  allowSaveInvalidEntries?: boolean;
  allowQuickCreate?: boolean;
  allowDuplicateEntry?: boolean;
  showWorkflowAction?: boolean;
  showSidebar?: boolean;
}

export class ViewAfterSaveAction {
  action: AfterSaveActionEnum;
  message?: LocalizedValueModel;
}
export class DataViewDisplayConfiguration {
  allowFilters?: boolean;
  allowMetaDataFilter?: boolean;
  allowDetailsMetaDataFilter?: boolean;
  allowCurrentUserFilter?: boolean;
  allowChangeSorting?: boolean;
  allowCurrentUserGroupsFilter?: boolean;
  allowObsoleteFilter?: boolean;
  allowInvalidFilter?: null | boolean;
  allowInvalidRequirementsFilter?: null | boolean;
  allowSavedQueries?: boolean;
  allowSearch?: boolean;
  allowStateFilter?: boolean;
  allowUserCollections?: boolean;
  availableDisplays?: Array<DataViewAvailableDisplayModel>;
  enabled: boolean;
  filterFirst?: DataViewFilterFirstConfiguration;
  allowFilterCurrentStep: boolean;
  allowFilterPreviousSteps: boolean;
  orderable?: boolean;
  allowedTemplates?: Array<string>;
  quickFilters?: DataViewQuickFilter[];
  defaultSorting?: Sort[];
  entryClassificationStyles?: DataViewConfigurationClassificationStylesModel;
  tableRowClickMode?: TableRowClickModeEnum;
}

export class DataViewFilterFirstConfiguration {
  autoFillForDataEntry?: boolean;
  enabled?: boolean;
  fields?: Array<{ fieldName: string; displayName?: any }>;
  matchingRule?: DataViewFilterFirstMatchingRule;
}
export class DataViewFilterFirstMatchingRule {
  customQuery: FilterExpression;
  type: FilterFirstMatchingTypeEnum;
}
export enum FilterFirstMatchingTypeEnum {
  And = 0,
  Or = 1,
  CustomQuery = 2,
}
export interface DataViewAvailableDisplayModel {
  viewType: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  isDefault: boolean;
  configuration: DataViewFaceConfiguration;
}

export class DataViewFaceConfiguration {
  badgeViewConfiguration?: DataViewBadgeFaceConfiguration;
  calendarViewConfiguration?: DataViewCalendarFaceConfiguration;
  kanbanViewConfiguration?: DataViewKanbanFaceConfiguration;
  miniCardViewConfiguration?: DataViewMiniCardFaceConfiguration;
  tableViewConfiguration?: DataViewTableFaceConfiguration;
  timelineViewConfiguration?: DataViewTimelineFaceConfiguration;
  treeViewConfiguration?: DataViewTreeFaceConfiguration;
}

export interface DataViewBadgeFaceConfiguration {
  fieldsMapping: Pick<
    DataViewFieldsMappingModel,
    'mainTitle' | 'subTitle' | 'picture' | 'mainNumberField'
  >;
  showMetaData?: boolean;
}

export type DataViewCalendarFaceConfiguration = {
  fieldsMapping: Pick<
    DataViewFieldsMappingModel,
    'dateFields' | 'mainTitle' | 'subTitle' | 'picture'
  >;
};

export type DataViewKanbanFaceConfiguration = {
  fieldsMapping: Pick<
    DataViewFieldsMappingModel,
    | 'categoryField'
    | 'subCategoryField'
    | 'mainTitle'
    | 'subTitle'
    | 'picture'
    | 'mainDateField'
    | 'relationField'
    | 'mainNumberField'
  >;
  showMetaData?: boolean;
  reorderable?: boolean;
  categories?: Array<string>;
};

export type DataViewMiniCardFaceConfiguration = {
  fieldsMapping: Pick<
    DataViewFieldsMappingModel,
    'dateField' | 'mainTitle' | 'subTitle' | 'picture'
  >;
};

export class DataViewTableFaceConfiguration {
  allowChangeFields: boolean;
  fields: Array<string>;
  maxDisplayedValues?: TableViewMaxDisplayedValuesConfig;
}

export type TableViewMaxDisplayedValuesConfig = {
  repeatableField?: number;
  multiValueField?: number;
};

export class DataViewTimelineFaceConfiguration {
  /**
   * Whether the view allow pin posts or not
   */
  allowPinPost?: boolean;

  /**
   * Whether the view allow publish posts or not
   */
  allowPublishPost?: boolean;
  enabled?: boolean;
}

export type DataViewTreeFaceConfiguration = {
  fieldsMapping: Pick<
    DataViewFieldsMappingModel,
    'icon' | 'mainTitle' | 'subTitle' | 'reference' | 'treeReference'
  >;
  traceParents?: boolean;
};

export interface DataViewFieldsMappingModel {
  mainTitle?: string;
  picture?: string;
  subTitle?: string;
  categoryField: string;
  mainDateField?: string;
  relationField?: string;
  subCategoryField?: string;
  dateField?: string;
  icon?: string;
  reference?: string;
  treeReference: string;
  dateFields: Array<string>;
  mainNumberField?: string;
}

export const DISCUSSION_DEFAULT_CONFIG: DataViewEntriesDiscussionConfiguration =
  {
    enabled: true,
    allowAddComments: true,
    showWorkflowComments: false,
    allowMentions: false,
    allowAddAttachments: {
      enabled: true,
      maxSize: MAX_FILE_SIZE_IN_BYTES,
      allowedExtensions: ALLOWED_MIME_TYPES.map((mime) => mime.value),
    },
    limitCommentsToCurrentView: false,
  };
export const HISTORY_DEFAULT_CONFIG: DataViewEntryHistoryConfiguration = {
  enabled: true,
  showChanger: true,
  showChanges: true,
  showTriggerHistory: {
    enabled: true,
    showTriggerDetails: true,
  },
};

export interface DataViewConfigurationClassificationStylesModel {
  backgroundColor?: null | DataViewConfigurationClassificationStylesSingleConfigModel;
  borderColor?: null | DataViewConfigurationClassificationStylesSingleConfigModel;
  indicatorIcon?: null | DataViewConfigurationClassificationStylesSingleConfigModel;
  tags?: null | DataViewConfigurationClassificationStylesMultiConfigModel;
}
export interface DataViewConfigurationClassificationStylesSingleConfigModel {
  selectedOption: null | RefProperty;
  allowChanging: boolean;
  enabledOptions: Array<RefProperty>;
}

export interface DataViewConfigurationClassificationStylesMultiConfigModel {
  selectedOption: Array<RefProperty>;
  allowChanging: boolean;
  enabledOptions: Array<RefProperty>;
}

export enum DisplayRelationAsEnum {
  Tab = 0,
  SubView = 1,
  Field = 2,
}
export enum RelationSubViewModeEnum {
  EntriesList = 0,
  EntryDetails = 1,
}
export enum ViewImplicitType {
  NONE = 0,
  INBOX = 1,
  MY_REQUEST_DETAILS = 2,
}
export enum RelationOpenEntryAs {
  Sidebar = 0,
  Full = 1,
  NewTab = 2,
  NewWindow = 3,
}
export class DataViewQrCodeConfigurationModel {
  enabled: boolean;
}
export interface ViewSourceOptions {
  isFilterFirstEnabled?: boolean;
  isParameterized?: boolean;
  sourceParameters?: ViewSourceParameter[];
}

export interface ViewSourceParameter {
  name: string;
  valueType: ViewSourceParameterValueTypeEnum;
  value?: any;
}

export enum ViewSourceParameterValueTypeEnum {
  Fixed = 0,
  Expression = 1,
  FilterFirst = 2,
}
export type DataViewFilterFirstConfigurationDto = {
  enabled?: boolean;
  fields?: Array<DataViewFilterFirstFieldModel>;
  autoFillForDataEntry?: boolean;
  matchingRule?: null | DataViewFilterFirstMatchingRule;
};

export enum DefaultSidebarEnum {
  None = 0, // default
  Information = 1,
  WorkflowPath = 2,
  Discussion = 3,
  History = 4,
  Emails = 5,
}
export interface ViewDisplayOptions {
  transparent?: boolean;
  fullPage?: boolean;
  outerSpacing?: boolean;
  showPageHeader?: boolean;
  pageContainedWidth?: boolean;
}
